import { forwardRef, type SelectHTMLAttributes } from "react";
import { clsx } from "clsx";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, error, options, className, id, ...props }, ref) => {
    const selectId = id || (label ? `select-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined);
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-[var(--color-text-primary)]">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          aria-invalid={!!error}
          className={clsx(
            "h-10 px-3 rounded-md border bg-[var(--color-surface-base)] text-[var(--color-text-primary)] text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
            error
              ? "border-[var(--color-danger-500)]"
              : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)]",
            className,
          )}
          {...props}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error ? (
          <span className="text-xs text-[var(--color-danger-500)]">{error}</span>
        ) : hint ? (
          <span className="text-xs text-[var(--color-text-secondary)]">{hint}</span>
        ) : null}
      </div>
    );
  },
);
Select.displayName = "Select";
